// ✅ src/pages/VehicleV2/JobCardEntryDetailReportAllPrint.jsx
// Logo: src/assets/infraLogo.jpg

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import companyLogo from "../../assets/infraLogo.jpg";

const API_BASE = "https://nissi-india.com/api/jobCardEntryReport";

const toInt = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : d;
};
const toStr = (v) => String(v ?? "").trim();
const isSelectLike = (v) => {
  const s = toStr(v).toLowerCase();
  return !s || s === "select" || s === "all";
};
const pretty = (v) => (isSelectLike(v) ? "ALL" : toStr(v));
const display = (v) =>
  v === undefined || v === null || String(v).trim() === "" ? "—" : v;

export default function JobCardEntryDetailReportAllPrint() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const qs = useMemo(() => new URLSearchParams(search), [search]);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  const page = toInt(qs.get("page"), 1);
  const pageSize = toInt(qs.get("pageSize"), 1000);

  const finYear =
    typeof window !== "undefined" ? localStorage.getItem("finYear") || "" : "";

  const mapRow = (r, idx) => {
    const sNo = (page - 1) * pageSize + idx + 1;

    return {
      sNo,
      vehicleMainType: r.fld_vehicletypename ?? r.vehicleMainType ?? "",
      jobCardNo: r.fld_jobcardno ?? r.jobCardNo ?? "",
      entryDate: r.fld_entrydate ?? r.entryDate ?? "",
      vehicleName: r.fld_vehicle_name ?? r.vehicleName ?? "",
      vehicleRegNo: r.fld_vehicle_regno ?? r.vehicleRegNo ?? "",
      location: r.fld_branchname ?? r.location ?? r.fld_location ?? "",
      hrKm:
        r.fld_hrkm ??
        r.fld_workinghrs ??
        r.fld_workingkm ??
        r.fld_hrs ??
        r.hrKm ??
        "",
      status: r.fld_status ?? r.status ?? "",
      complaint: r.fld_natureofcomplaint ?? r.complaint ?? "",
      attendedBy: r.fld_complaintattendedby ?? r.attendedBy ?? "",
      inspectedBy: r.fld_inspectedby ?? r.inspectedBy ?? "",
      remedy: r.fld_remedy ?? r.remedy ?? "",
      fromDate: r.fld_fromdate ?? r.fromDate ?? "",
      inTime: r.fld_intime ?? r.inTime ?? "",
      toDate: r.fld_todate ?? r.toDate ?? "",
      outTime: r.fld_outtime ?? r.outTime ?? "",
      hrs:
        r.fld_hours ??
        r.fld_hrs ??
        r.fld_maintainhrs ??
        r.fld_complainthrs ??
        r.hrs ??
        "",
      sparesDetails: r.fld_sparedetails ?? r.sparesDetails ?? "",
      maintenanceBy: r.fld_maintenanceby ?? r.maintenanceBy ?? "",
    };
  };

  const fetchPrintData = async () => {
    try {
      setLoading(true);

      const params = Object.fromEntries(qs.entries());
      const res = await axios.get(`${API_BASE}/list`, { params });

      if (!res.data?.success) throw new Error(res.data?.message || "Failed");

      const dataRows = (res.data.rows || []).map(mapRow);
      setRows(dataRows);
    } catch (e) {
      setRows([]);
      alert(e?.message || "Failed to load print data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrintData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handlePrint = () => window.print();

  const currentDate = useMemo(
    () =>
      new Date().toLocaleDateString("en-IN", {
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
    []
  );

  const currentDateTime = useMemo(
    () =>
      new Date().toLocaleString("en-IN", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }),
    []
  );

  const fromDate = pretty(qs.get("fromDate"));
  const toDate = pretty(qs.get("toDate"));
  const dateRange =
    isSelectLike(qs.get("fromDate")) && isSelectLike(qs.get("toDate"))
      ? "ALL"
      : `${qs.get("fromDate") || "—"} to ${qs.get("toDate") || "—"}`;

  return (
    <div className="report-page min-h-screen bg-white">
      <style jsx global>{`
        :root {
          --navy: #163a5f;
          --navy-deep: #0f2a45;
          --gold: #e8a317;
          --slate: #5b6b82;
          --line: #c5ced8;
          --ink: #1c2430;
        }

        .report-page {
          color: var(--ink);
          font-family: "Segoe UI", Calibri, Arial, sans-serif;
        }

        .letterhead {
          border: 1.5px solid var(--navy);
          overflow: hidden;
          background: #fff;
        }

        .letterhead-topbar {
          height: 6px;
          background: linear-gradient(90deg, #0f2a45 0%, #163a5f 55%, #e8a317 100%);
        }

        .letterhead-body {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 10px 14px 8px;
        }

        .logo-wrap {
          flex: 0 0 auto;
          width: 82px;
          height: 70px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .logo-wrap img {
          max-width: 82px;
          max-height: 70px;
          object-fit: contain;
          display: block;
        }

        .company-block {
          flex: 1 1 auto;
          text-align: center;
          min-width: 0;
        }

        .brand-name {
          font-size: 20px;
          letter-spacing: 1.4px;
          font-weight: 800;
          color: var(--navy);
          line-height: 1.1;
          margin: 0;
        }

        .legal-name {
          margin: 2px 0 0;
          font-size: 12px;
          font-weight: 600;
          color: #2c3e50;
        }

        .addr-line {
          margin: 3px 0 0;
          font-size: 10.5px;
          color: #4b5563;
          line-height: 1.4;
        }

        .meta-block {
          flex: 0 0 150px;
          text-align: right;
          font-size: 10.5px;
          color: #334155;
          line-height: 1.35;
        }

        .meta-label {
          display: block;
          font-size: 8.5px;
          letter-spacing: 0.7px;
          text-transform: uppercase;
          color: var(--slate);
          font-weight: 700;
        }

        .meta-value {
          display: block;
          font-weight: 700;
          color: var(--navy);
        }

        .letterhead-bottombar {
          height: 3px;
          background: var(--gold);
        }

        .doc-title-wrap {
          margin: 12px 0 8px;
          text-align: center;
        }

        .doc-title {
          margin: 0;
          font-size: 16px;
          font-weight: 800;
          letter-spacing: 1.2px;
          color: var(--navy);
        }

        .doc-sub {
          margin: 2px 0 0;
          font-size: 10.5px;
          color: var(--slate);
        }

        .title-rule {
          width: 80px;
          height: 3px;
          background: var(--gold);
          margin: 6px auto 0;
        }

        .filter-strip {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          border: 1px solid var(--line);
          background: #f6f8fb;
          margin-bottom: 10px;
        }

        .filter-cell {
          padding: 6px 8px;
          border-right: 1px solid var(--line);
        }

        .filter-cell:last-child {
          border-right: none;
        }

        .filter-k {
          display: block;
          font-size: 8px;
          font-weight: 700;
          letter-spacing: 0.6px;
          text-transform: uppercase;
          color: var(--slate);
        }

        .filter-v {
          display: block;
          margin-top: 1px;
          font-size: 11px;
          font-weight: 700;
          color: var(--navy);
          word-break: break-word;
        }

        .rpt-table {
          width: 100%;
          border-collapse: collapse;
        }

        .rpt-table th {
          background: var(--navy);
          color: #fff;
          font-size: 7px;
          letter-spacing: 0.1px;
          font-weight: 700;
          padding: 5px 2px;
          border: 1px solid var(--navy-deep);
          text-align: center;
          line-height: 1.15;
        }

        .rpt-table td {
          border: 1px solid #d0d7e0;
          padding: 3px 2px;
          font-size: 7.5px;
          color: #1f2937;
          text-align: center;
          vertical-align: top;
        }

        .rpt-table td.left {
          text-align: left;
        }

        .rpt-table tbody tr:nth-child(even) {
          background: #f7f9fc;
        }

        .sign-row {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          margin-top: 16px;
          gap: 24px;
        }

        .gen-by {
          font-size: 10.5px;
          color: #475569;
          line-height: 1.45;
        }

        .sign-box {
          width: 190px;
          text-align: center;
        }

        .sign-line {
          border-top: 1px solid #64748b;
          padding-top: 5px;
          font-size: 11px;
          font-weight: 700;
          color: var(--navy);
        }

        .sign-hint {
          font-size: 9.5px;
          color: #64748b;
          margin-top: 2px;
        }

        .rpt-footer {
          margin-top: 12px;
          border-top: 1px solid var(--line);
          padding-top: 7px;
          display: flex;
          justify-content: space-between;
          font-size: 9.5px;
          color: #64748b;
        }

        .disclaimer {
          text-align: center;
          font-size: 9.5px;
          color: #64748b;
          margin-top: 6px;
        }

        @media print {
          @page {
            size: A4 landscape;
            margin: 6mm 5mm 7mm 5mm;
          }

          html,
          body {
            background: #fff !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }

          .no-print {
            display: none !important;
          }

          .report-sheet {
            max-width: none !important;
            margin: 0 !important;
            padding: 0 !important;
          }

          .letterhead,
          .filter-strip,
          .sign-row {
            break-inside: avoid;
          }

          .rpt-table thead {
            display: table-header-group;
          }

          .rpt-table tbody tr {
            break-inside: avoid;
            page-break-inside: avoid;
          }

          .rpt-table th {
            background: #163a5f !important;
            color: #fff !important;
          }

          .rpt-table tbody tr:nth-child(even) {
            background: #f7f9fc !important;
          }
        }
      `}</style>

      <div className="no-print py-5 text-center flex gap-3 justify-center">
        <button
          onClick={() => navigate(-1)}
          className="bg-gray-200 text-black px-6 py-3 text-sm font-medium hover:bg-gray-300 rounded"
          type="button"
        >
          Back
        </button>
        <button
          onClick={handlePrint}
          className="bg-[#163a5f] text-white px-6 py-3 text-sm font-medium hover:bg-[#0f2a45] transition-colors rounded"
          disabled={loading}
          type="button"
        >
          {loading ? "Loading..." : "Print Report"}
        </button>
      </div>

      <div className="report-sheet max-w-7xl mx-auto px-4 pb-8 print:max-w-none print:mx-0 print:px-0 print:pb-0">
        <header className="letterhead">
          <div className="letterhead-topbar" />
          <div className="letterhead-body">
            <div className="logo-wrap">
              <img
                src={companyLogo}
                alt="INFRACONSOFT"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            </div>
            <div className="company-block">
              <h1 className="brand-name">INFRACONSOFT</h1>
              <p className="legal-name">
                Nissi Infotech &nbsp;|&nbsp; Nissi Civil Construction
              </p>
              <p className="addr-line">
                3/42-12, Nagappa Colony, Kavundampalayam — 641030, Tamil Nadu, India
                <br />
                Phone: +91 90037 44644 &nbsp;·&nbsp;
                infraconsoft-implementation@nissiinfotech.in
              </p>
            </div>
            <div className="meta-block">
              <span className="meta-label">Report Date</span>
              <span className="meta-value">{currentDate}</span>
              <span className="meta-label" style={{ marginTop: 7 }}>
                Financial Year
              </span>
              <span className="meta-value">{finYear || "—"}</span>
              <span className="meta-label" style={{ marginTop: 7 }}>
                Document
              </span>
              <span className="meta-value">JCE / Job Card Detail</span>
            </div>
          </div>
          <div className="letterhead-bottombar" />
        </header>

        <div className="doc-title-wrap">
          <h2 className="doc-title">JOB CARD ENTRY DETAIL REPORT</h2>
          <p className="doc-sub">Workshop job cards — filtered listing</p>
          <div className="title-rule" />
        </div>

        <section className="filter-strip">
          <div className="filter-cell">
            <span className="filter-k">Period</span>
            <span className="filter-v">{dateRange}</span>
          </div>
          <div className="filter-cell">
            <span className="filter-k">Branch / Location</span>
            <span className="filter-v">
              {pretty(qs.get("branch") || qs.get("location"))}
            </span>
          </div>
          <div className="filter-cell">
            <span className="filter-k">Status</span>
            <span className="filter-v">{pretty(qs.get("status"))}</span>
          </div>
          <div className="filter-cell">
            <span className="filter-k">Page</span>
            <span className="filter-v">
              {page} / Size {pageSize}
            </span>
          </div>
          <div className="filter-cell">
            <span className="filter-k">Records</span>
            <span className="filter-v">{loading ? "…" : rows.length}</span>
          </div>
        </section>

        <table className="rpt-table">
          <thead>
            <tr>
              <th>S. No</th>
              <th>Vehicle Main Type</th>
              <th>Job Card No</th>
              <th>Entry Date</th>
              <th>Vehicle Name</th>
              <th>Vehicle Reg No</th>
              <th>Location</th>
              <th>Hr/Km</th>
              <th>Status</th>
              <th>Nature Of Complaint</th>
              <th>Complaint Attended By</th>
              <th>Inspected By</th>
              <th>Remedy</th>
              <th>From Date</th>
              <th>In Time</th>
              <th>To Date</th>
              <th>Out Time</th>
              <th>Hrs</th>
              <th>Spares Details</th>
              <th>Maintenance By</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((x) => (
              <tr key={x.sNo}>
                <td>{x.sNo}</td>
                <td className="left">{display(x.vehicleMainType)}</td>
                <td>{display(x.jobCardNo)}</td>
                <td>{display(x.entryDate)}</td>
                <td className="left">{display(x.vehicleName)}</td>
                <td>{display(x.vehicleRegNo)}</td>
                <td className="left">{display(x.location)}</td>
                <td>{display(x.hrKm)}</td>
                <td>{display(x.status)}</td>
                <td className="left">{display(x.complaint)}</td>
                <td className="left">{display(x.attendedBy)}</td>
                <td className="left">{display(x.inspectedBy)}</td>
                <td className="left">{display(x.remedy)}</td>
                <td>{display(x.fromDate)}</td>
                <td>{display(x.inTime)}</td>
                <td>{display(x.toDate)}</td>
                <td>{display(x.outTime)}</td>
                <td>{display(x.hrs)}</td>
                <td className="left">{display(x.sparesDetails)}</td>
                <td className="left">{display(x.maintenanceBy)}</td>
              </tr>
            ))}

            {rows.length === 0 && !loading && (
              <tr>
                <td colSpan={20}>No records</td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="sign-row">
          <div className="gen-by">
            <strong style={{ color: "#163a5f" }}>Prepared by</strong>
            <br />
            Infraconsoft ERP
            <br />
            Automated job card entry detail
            <br />
            Generated: {currentDateTime}
          </div>
          <div className="sign-box">
            <div style={{ height: 36 }} />
            <div className="sign-line">Authorized Signatory</div>
            <div className="sign-hint">Workshop / Fleet Administration</div>
          </div>
        </div>

        <div className="rpt-footer">
          <span>
            INFRACONSOFT · ERP Software · www.infraconsoft.com · owned by
            www.nissiinfotech.com
          </span>
          <span>Confidential — Internal Use Only</span>
        </div>
        <p className="disclaimer">
          This is a computer-generated report. No physical signature is required
          for authenticity unless mandated by company policy.
        </p>
      </div>
    </div>
  );
}